/**
 * Admin ban-appeals queue. 14-day review SLA per plan §Compliance &
 * Privacy §Ban appeals. List filtered by status (default pending).
 * Decide flow: senior-moderator+ accepts (un-bans + audit_log) or
 * denies (ban reaffirmed) with a required review note.
 *
 * Server returns 400 with reason="not_pending" or "insufficient_role"
 * which we surface inline. The 'open' filter is moderation-service's
 * union of pending + reviewed-recently for the at-a-glance dashboard.
 */

import { Badge, type BadgeVariant } from '@funkihoo/ui-web';
import { useState } from 'react';
import {
  type AppealStatusFilter,
  type BanAppealRow,
  useBanAppealsQuery,
  useDecideBanAppealMutation,
} from '../api/ban-appeals.js';

const STATUS_TABS: { value: AppealStatusFilter; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'open', label: 'Open' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'denied', label: 'Denied' },
];

export function BanAppealsRoute() {
  const [filter, setFilter] = useState<AppealStatusFilter>('pending');
  const [activeId, setActiveId] = useState<string | null>(null);
  const query = useBanAppealsQuery(filter);

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">Ban appeals</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Banned users submitting appeals. 14-day review SLA. Accept (un-ban + audit log) or deny
          (ban reaffirmed) with a required review note.
        </p>
      </header>

      <div className="flex items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => {
              setFilter(t.value);
              setActiveId(null);
            }}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
              filter === t.value
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            {t.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => query.refetch()}
          className="ml-auto rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          Refresh
        </button>
      </div>

      {query.isLoading && <p className="text-sm text-neutral-500">Loading appeals…</p>}
      {query.isError && (
        <p className="text-sm text-danger-600">
          Could not load appeals. Backend down? Check moderation-service on :8008.
        </p>
      )}
      {query.data && query.data.appeals.length === 0 && (
        <p className="text-sm text-neutral-500">No appeals in this view.</p>
      )}

      {query.data && query.data.appeals.length > 0 && (
        <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
          {query.data.appeals.map((a) => (
            <li key={a.id}>
              <AppealRow
                appeal={a}
                expanded={activeId === a.id}
                onToggle={() => setActiveId((prev) => (prev === a.id ? null : a.id))}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AppealRow({
  appeal,
  expanded,
  onToggle,
}: {
  appeal: BanAppealRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const slaMs = new Date(appeal.slaDeadline).getTime() - Date.now();
  const slaHours = Math.round(slaMs / 36e5);
  const slaCls =
    slaHours < 0 ? 'text-danger-700' : slaHours < 24 ? 'text-warning-700' : 'text-neutral-500';
  const decide = useDecideBanAppealMutation();
  const [note, setNote] = useState('');

  return (
    <div className="px-3 py-3 hover:bg-neutral-50">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between text-left"
      >
        <div>
          <p className="text-sm font-medium text-neutral-900">
            Appeal from user <span className="font-mono">{appeal.userId.slice(0, 8)}</span>
            <StatusBadge status={appeal.status} />
          </p>
          <p className={`mt-0.5 text-xs ${slaCls}`}>
            SLA {slaHours < 0 ? `overdue by ${-slaHours}h` : `${slaHours}h remaining`} ·{' '}
            <span className="text-neutral-500">
              filed {new Date(appeal.createdAt).toLocaleString()}
            </span>
          </p>
        </div>
        <span className="text-xs text-neutral-400">{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3 rounded-md border border-neutral-200 bg-white p-3">
          <Section title="User's statement">
            <p className="whitespace-pre-wrap text-sm text-neutral-700">{appeal.statement}</p>
          </Section>
          {appeal.idEvidenceS3Key && (
            <Section title="ID evidence">
              <p className="font-mono text-xs text-neutral-500">{appeal.idEvidenceS3Key}</p>
              <p className="mt-1 text-xs text-neutral-500">
                Open in admin S3 console to view (presigned-URL UI lands separately).
              </p>
            </Section>
          )}
          {appeal.status === 'pending' ? (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Review note (required, audit-logged; user sees this on decision email)"
                rows={3}
                className="w-full rounded-md border border-neutral-300 p-2 text-sm"
                minLength={1}
                required
              />
              {decide.error && (
                <p className="text-sm text-danger-600">{(decide.error as Error).message}</p>
              )}
              {decide.data && (
                <p className="text-sm text-success-700">Decision recorded: {decide.data.status}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={decide.isPending || note.trim().length < 1}
                  onClick={() =>
                    decide.mutate({ id: appeal.id, decision: 'accept', note: note.trim() })
                  }
                  className="rounded-md bg-success-600 px-3 py-2 text-sm font-medium text-white hover:bg-success-700 disabled:opacity-50"
                >
                  {decide.isPending && decide.variables?.decision === 'accept'
                    ? 'Accepting…'
                    : 'Accept (un-ban)'}
                </button>
                <button
                  type="button"
                  disabled={decide.isPending || note.trim().length < 1}
                  onClick={() =>
                    decide.mutate({ id: appeal.id, decision: 'deny', note: note.trim() })
                  }
                  className="rounded-md bg-danger-600 px-3 py-2 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-50"
                >
                  {decide.isPending && decide.variables?.decision === 'deny'
                    ? 'Denying…'
                    : 'Deny (ban reaffirmed)'}
                </button>
              </div>
            </form>
          ) : (
            <Section title="Decision">
              <p className="text-sm text-neutral-700">
                <span className="font-medium">{appeal.status}</span>
                {appeal.reviewedAt && (
                  <span className="ml-2 text-neutral-500">
                    on {new Date(appeal.reviewedAt).toLocaleString()}
                  </span>
                )}
              </p>
              {appeal.reviewNote && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-700">
                  {appeal.reviewNote}
                </p>
              )}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-sm font-medium text-neutral-700">{title}</h3>
      <div className="mt-1">{children}</div>
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: BadgeVariant =
    status === 'pending'
      ? 'warning'
      : status === 'accepted'
        ? 'success'
        : status === 'denied'
          ? 'danger'
          : 'neutral';
  return (
    <Badge variant={variant} className="ml-2">
      {status}
    </Badge>
  );
}
